/**
 * Constantes centralizadas para claves de Storage
 * Evita inconsistencias entre diferentes servicios
 */

// ==========================================
// CLAVES DE USUARIO
// ==========================================
export const USER_STORAGE_KEYS = {
  HAS_LOGGED_IN: 'hasLoggedIn',
  HAS_SEEN_TUTORIAL: 'hasSeenTutorial',
  USERNAME: 'username',
  USER_CED: 'userced',
  USER_ID: 'userid',
  USER_OTHER: 'userother',
  FAVORITES: 'FAVORITOS',
  DARK_MODE: 'darkMode'
} as const;

// ==========================================
// CLAVES DE EVENTO
// ==========================================
export const EVENT_STORAGE_KEYS = {
  EVENT_CODE: 'eventcode',
  EVENT_NAME: 'eventName',
  EVENT_START_DATE: 'eventStartDate',
  EVENT_END_DATE: 'eventEndDate',
  EVENT_LOCATION: 'eventLocation'
} as const;

// ==========================================
// CLAVES DE DATOS DE CONFERENCIA
// ==========================================
export const CONFERENCE_STORAGE_KEYS = {
  DATA_LOADED: 'dataLoaded',
  SCHEDULE_DATA: 'scheduleData',
  SPEAKERS_DATA: 'speakersData',
  LOCATIONS_DATA: 'locationsData',
  LAST_UPDATE: 'lastUpdate',
  SCHEDULE_VERSION: 'scheduleVersion'
} as const;

// ==========================================
// CLAVES DE CONFIGURACIÓN
// ==========================================
export const CONFIG_STORAGE_KEYS = {
  APP_VERSION: 'appVersion',
  FIRST_LAUNCH: 'firstLaunch',
  LAST_SYNC: 'lastSync'
} as const;

// ==========================================
// CLAVES DE NETWORKING
// ==========================================
export const NETWORKING_STORAGE_KEYS = {
  SCANNED_USERS: 'scannedUsers',
  MY_QR_DATA: 'myQrData',
  NETWORKING_ENABLED: 'networkingEnabled'
} as const;

// ==========================================
// TODAS LAS CLAVES (para operaciones masivas)
// ==========================================
export const ALL_STORAGE_KEYS = {
  ...USER_STORAGE_KEYS,
  ...EVENT_STORAGE_KEYS,
  ...CONFERENCE_STORAGE_KEYS,
  ...CONFIG_STORAGE_KEYS,
  ...NETWORKING_STORAGE_KEYS
} as const;

// ==========================================
// TIPOS PARA TYPESCRIPT
// ==========================================
export type UserStorageKey = keyof typeof USER_STORAGE_KEYS;
export type EventStorageKey = keyof typeof EVENT_STORAGE_KEYS;
export type ConferenceStorageKey = keyof typeof CONFERENCE_STORAGE_KEYS;
export type ConfigStorageKey = keyof typeof CONFIG_STORAGE_KEYS;
export type NetworkingStorageKey = keyof typeof NETWORKING_STORAGE_KEYS;
export type AllStorageKey = keyof typeof ALL_STORAGE_KEYS;

// ==========================================
// FUNCIONES DE UTILIDAD
// ==========================================

/**
 * Obtiene todas las claves de una categoría específica
 */
export const getStorageKeys = (category: 'user' | 'event' | 'conference' | 'config' | 'networking' | 'all'): string[] => {
  switch (category) {
    case 'user':
      return Object.values(USER_STORAGE_KEYS);
    case 'event':
      return Object.values(EVENT_STORAGE_KEYS);
    case 'conference':
      return Object.values(CONFERENCE_STORAGE_KEYS);
    case 'config':
      return Object.values(CONFIG_STORAGE_KEYS);
    case 'networking':
      return Object.values(NETWORKING_STORAGE_KEYS);
    case 'all':
      return Object.values(ALL_STORAGE_KEYS);
    default:
      return [];
  }
};

/**
 * Verifica si una clave pertenece a una categoría específica
 */
export const isStorageKeyOfCategory = (
  key: string, 
  category: 'user' | 'event' | 'conference' | 'config' | 'networking'
): boolean => {
  const categoryKeys = getStorageKeys(category);
  return categoryKeys.includes(key);
};

// ==========================================
// CONSTANTES LEGACY (para compatibilidad)
// ==========================================
// Estas constantes mantienen compatibilidad con código existente
// Gradualmente se pueden migrar a usar las nuevas constantes

export const HAS_LOGGED_IN = USER_STORAGE_KEYS.HAS_LOGGED_IN;
export const HAS_SEEN_TUTORIAL = USER_STORAGE_KEYS.HAS_SEEN_TUTORIAL;
export const USERNAME = USER_STORAGE_KEYS.USERNAME;
export const USERCED = USER_STORAGE_KEYS.USER_CED;
export const USERID = USER_STORAGE_KEYS.USER_ID;
export const USEROTHER = USER_STORAGE_KEYS.USER_OTHER;
export const FAVORITES = USER_STORAGE_KEYS.FAVORITES;
export const EVENTCODE = EVENT_STORAGE_KEYS.EVENT_CODE;
export const EVENT_NAME = EVENT_STORAGE_KEYS.EVENT_NAME;
export const EVENT_START_DATE = EVENT_STORAGE_KEYS.EVENT_START_DATE;
export const EVENT_END_DATE = EVENT_STORAGE_KEYS.EVENT_END_DATE;
export const EVENT_LOCATION = EVENT_STORAGE_KEYS.EVENT_LOCATION;

// Claves de datos de conferencia unificadas
export const DATA_LOADED_KEY = CONFERENCE_STORAGE_KEYS.DATA_LOADED;
export const SCHEDULE_DATA_KEY = CONFERENCE_STORAGE_KEYS.SCHEDULE_DATA;
export const SPEAKERS_DATA_KEY = CONFERENCE_STORAGE_KEYS.SPEAKERS_DATA;
export const LOCATIONS_DATA_KEY = CONFERENCE_STORAGE_KEYS.LOCATIONS_DATA;
export const LAST_UPDATE_KEY = CONFERENCE_STORAGE_KEYS.LAST_UPDATE;
export const EVENTCODE_KEY = EVENT_STORAGE_KEYS.EVENT_CODE; // Alias para compatibilidad
